import { createFileRoute, Link, useRouter, useSearch } from '@tanstack/react-router';

import type React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { usePostAuthLoginMutation } from '@/services';
import { toast } from '@/components/ui/toaster';
import { z } from 'zod';
import { PasswordInput } from '@/components/ui/password-input';
import { useAuth } from '@/contexts/auth-context';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_unauthenticated/login')({
  component: LoginPage,
  validateSearch: z.object({
    redirect: z.string().optional(),
  }),
});

function LoginPage() {
  const { redirect } = useSearch({ from: '/_unauthenticated/login' });
  const { isSignupDisabled } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const [loginMutation, { isLoading: isLoggingIn }] = usePostAuthLoginMutation();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await loginMutation({
        body: {
          email,
          password,
        },
      });

      if (error) {
        toast.error('Invalid email or password. Please try again.');
      } else {
        toast.success('Login successful!');
        console.log('Redirecting to:', redirect || '/dashboard');
        router.navigate({
          to: redirect || '/dashboard',
          search: {
            redirect: undefined,
          },
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-chart-1/20 to-chart-2/20 dark:from-primary/40 dark:via-chart-1/40 dark:to-chart-2/40" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.08)_1px,transparent_0)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_70%)]" />

      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-chart-1/10 rounded-full blur-xl animate-pulse" />
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-chart-2/10 rounded-full blur-xl animate-pulse delay-700" />
      <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-primary/10 rounded-full blur-xl animate-pulse delay-300" />

      <div className="relative w-full max-w-md space-y-8">
        {/* Card Container */}
        <div className="bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl p-8 shadow-2xl">
          <div className="space-y-2 text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-chart-1 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <div className="w-4 h-4 bg-white rounded-sm" />
              </div>
            </div>
            <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Welcome back
            </h1>
            <p className="text-lg text-muted-foreground">Sign in to your IoT dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-foreground">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 bg-background/50 border-border/70 focus:border-primary/50 focus:ring-primary/20 transition-all duration-200"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-foreground">
                  Password
                </label>
                <PasswordInput
                  id="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 bg-background/50 border-border/70 focus:border-primary/50 focus:ring-primary/20 transition-all duration-200"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || isLoggingIn}
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-primary to-chart-1 hover:from-primary/90 hover:to-chart-1/90 shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {loading || isLoggingIn ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>

            <div
              className={cn('flex flex-col gap-3 text-center text-muted-foreground', {
                hidden: isSignupDisabled,
              })}
            >
              <span>
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  search={{ redirect }}
                  className="text-primary hover:text-primary/80 font-medium transition-colors duration-200"
                >
                  Sign up
                </Link>
              </span>
              <span>
                <Link
                  to="/forgot-password"
                  className="text-primary hover:text-primary/80 font-medium transition-colors duration-200"
                >
                  Forgot password?
                </Link>
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
